import { useEffect, useRef, useState } from "react";

import {
  Button,
  DocumentList,
  InfoPanel,
  Select,
  SelectData,
  useStepperDispatch,
  TextField,
  Typography,
  Checkbox,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";
import { nameof, isEmpty } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useCheckOtherParentDataMutation } from "../../../api/personsApi";
import { useTypedSelector } from "../../../app/rootReducer";

import { useCurrentUser } from "../../../hooks/authorization/useCurrentUser";
import { useGetDocumentTypes } from "../../../hooks/codebook/useGetDocumentTypes";
import { useGetParentshipTypes } from "../../../hooks/codebook/useGetParentshipTypes";
import { useGetProfessionalQualifications } from "../../../hooks/codebook/useGetProfessionalQualifications";
import { useGetReasonNonExistenceOibTypes } from "../../../hooks/codebook/useGetReasonNonExistenceOibTypes";
import { useGetSexes } from "../../../hooks/codebook/useGetSexes";
import useInput from "../../../hooks/input/useInput";
import { CheckOtherParentValidationSchema } from "../../../models/Person/CheckOtherParentValidationSchema";
import OtherParent, {
  CreateUpdateOtherParentInital,
} from "../../../models/Person/OtherParent";
import OtherParentDataCheckStatusEnum from "../../../models/Person/OtherParentDataCheckStatusEnum";
import OtherParentValidationSchema from "../../../models/Person/OtherParentValidationSchema";
import ParentshipTypeEnum from "../../../models/Person/ParentshipTypeEnum";
import CodeBook from "../../../models/Shared/CodeBook";
import {
  updateRequestOtherParent,
  selectAllDocuments,
} from "../../../slices/requestSlice";
import DocumentsModal from "../DocumentsModal/DocumentsModal";
import styles from "./OtherParentDataStep.module.scss";
import { useOtherParentDocuments } from "./useOtherParentDocuments";

export default function OtherParentDataStep() {
  const [otherParent, setOtherParent] = useState<OtherParent>(
    CreateUpdateOtherParentInital
  );
  const latestOtherParentRef = useRef(otherParent);

  const [showDataCheckMessage, setShowDataCheckMessage] = useState<boolean>(
    false
  );
  const [
    checkOtherParentData,
    { isLoading: isCheckingOtherParentData },
  ] = useCheckOtherParentDataMutation();

  const otherParentState = useTypedSelector(
    (state) => state.request.request.otherParent
  );
  const childState = useTypedSelector((state) => state.request.request.child);

  const user = useCurrentUser();

  const dispatch = useDispatch();
  const allDocuments = useSelector(selectAllDocuments);

  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  const [isMessageModalOpened, setIsMessageModalOpened] = useState(false);

  useEffect(() => {
    latestOtherParentRef.current = otherParent;
  }, [otherParent]);

  const handleToggleConfirmModal = () => {
    setIsConfirmModalOpened((prev) => !prev);
  };

  const handleConfirmActionModal = () => {
    handleStep();
  };

  const handleToggleMessageModal = () => {
    setIsMessageModalOpened((prev) => !prev);
  };

  const handleToggleIsOibNotExistent = () => {
    setOtherParent((prevState) => {
      return {
        ...CreateUpdateOtherParentInital,
        firstName: prevState.firstName,
        lastName: prevState.lastName,
        isOibNotExistent: !prevState.isOibNotExistent,
      };
    });
  };

  useEffect(() => {
    if (otherParentState && !isEmpty(otherParentState)) {
      if (otherParentState.integrationResultStatus) {
        setOtherParent({
          ...otherParentState,
          integrationResultStatus: otherParentState.integrationResultStatus,
          isCustodyConfirmed:
            otherParentState.isOibNotExistent === false &&
            otherParentState.integrationResultStatus ===
              OtherParentDataCheckStatusEnum.Success,
          isConfirmedParentChildRelationByDocument:
            otherParentState.isOibNotExistent === false &&
            (otherParentState.integrationResultStatus ===
              OtherParentDataCheckStatusEnum.Success ||
              otherParentState.isConfirmedParentChildRelationByDocument),
        });
      } else {
        setOtherParent(otherParentState);
      }
    }
  }, [otherParentState]);

  useEffect(() => {
    if (
      Object.keys(otherParent).length !== 0 &&
      !otherParent.isCustodyConfirmed &&
      (!otherParent?.documents || otherParent.documents?.length < 1) &&
      otherParent.isConfirmedParentChildRelationByDocument
    ) {
      const newOtherParent = {
        ...otherParent,
        isConfirmedParentChildRelationByDocument: false,
      };
      setOtherParent(newOtherParent);
    }
  }, [
    otherParent.documents,
    otherParent.isConfirmedParentChildRelationByDocument,
    otherParent.isCustodyConfirmed,
  ]);

  useStepperDispatch({
    type: "next",
    payload: () => {
      return handleStep();
    },
  });

  useStepperDispatch({
    type: "previous",
    payload: () => {
      return handleStep();
    },
  });

  const professionalQualifications = useGetProfessionalQualifications();
  const parentshipTypes = useGetParentshipTypes();
  const documentTypes = useGetDocumentTypes();
  const reasonNonExistenceOibTypes = useGetReasonNonExistenceOibTypes();
  const sexes = useGetSexes();

  const { t } = useTranslation();

  const { handleChange, handleMultipleChange } = useInput();

  const [errors, doValidations] = useValidation({
    data: otherParent,
    validationSchema: OtherParentValidationSchema,
  });

  const [checkDataErrors, validateCheckData] = useValidation({
    data: otherParent,
    validationSchema: CheckOtherParentValidationSchema,
  });

  const handleBlur = (e: any) => {
    const { name, value } = e.target;
    const nameString = name as string;
    if (otherParentState && nameString in otherParentState) {
      const oldValue = otherParentState[nameString as keyof OtherParent];

      if (value !== oldValue) {
        setOtherParent((prevState) => {
          return {
            ...prevState,
            integrationResultStatus: undefined,
          };
        });
      }
    }
  };

  const handleGetCheckMessage = () => {
    if (otherParent.integrationResultStatus) {
      if (
        otherParent.integrationResultStatus ===
        OtherParentDataCheckStatusEnum.Success
      ) {
        return (
          <InfoPanel
            message={t("otherParentDataStep.checkDataSuccessMessage")}
            color="success"
            className={styles.checkDataMessages}
          />
        );
      } else if (
        otherParent.integrationResultStatus ===
        OtherParentDataCheckStatusEnum.SameAsApplicantParent
      ) {
        return (
          <InfoPanel
            message={t(
              "otherParentDataStep.checkDataSameAsApplicantParentMessage"
            )}
            color="danger"
            className={styles.checkDataMessages}
          />
        );
      } else if (
        otherParent.integrationResultStatus ===
        OtherParentDataCheckStatusEnum.DataMismatch
      ) {
        return (
          <InfoPanel
            message={t("otherParentDataStep.checkDataMismatchMessage")}
            color="danger"
            className={styles.checkDataMessages}
          />
        );
      } else if (
        otherParent.integrationResultStatus ===
        OtherParentDataCheckStatusEnum.OibFailed
      ) {
        return (
          <InfoPanel
            message={t("otherParentDataStep.checkDataOibFailedMessage")}
            color="warning"
            className={styles.checkDataMessages}
          />
        );
      } else if (
        otherParent.integrationResultStatus ===
        OtherParentDataCheckStatusEnum.NoCustody
      ) {
        return (
          <InfoPanel
            message={t("otherParentDataStep.checkDataNoCustodyMessage")}
            color="warning"
            className={styles.checkDataMessages}
          />
        );
      }
    } else if (showDataCheckMessage) {
      return (
        <InfoPanel
          message={t("otherParentDataStep.checkDataMessage")}
          color="warning"
          className={styles.checkDataMessages}
        />
      );
    }
    return <></>;
  };

  const handleCheckData = async () => {
    if (validateCheckData()) {
      if (isCheckingOtherParentData) {
        return;
      }

      try {
        const checkedOtherParent = otherParent;
        dispatch(updateRequestOtherParent(checkedOtherParent));
        const result = await checkOtherParentData({
          firstName: checkedOtherParent.firstName,
          lastName: checkedOtherParent.lastName,
          oib: checkedOtherParent.oib,
          childOib: childState.oib,
        }).unwrap();
        const latestOtherParent = latestOtherParentRef.current;

        if (
          latestOtherParent.firstName !== checkedOtherParent.firstName ||
          latestOtherParent.lastName !== checkedOtherParent.lastName ||
          latestOtherParent.oib !== checkedOtherParent.oib
        ) {
          return;
        }

        dispatch(
          updateRequestOtherParent({
            ...latestOtherParent,
            integrationResultStatus: result.status,
            yearOfBirth: result.yearOfBirth,
          })
        );
      } catch {
        // Global API interceptor shows request errors.
      }
    } else {
      dispatch(
        updateRequestOtherParent({
          ...otherParent,
          integrationResultStatus: OtherParentDataCheckStatusEnum.DataMismatch,
        })
      );
    }
  };

  const handleParentshipTypeChange = (e: any) => {
    const { value } = e.target;
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      parentshipTypes,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof otherParent>("parentshipTypeId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof otherParent>("parentshipTypeName"),
        },
      ],
      setOtherParent
    );

    if (+value !== ParentshipTypeEnum.Custodian) {
      setOtherParent((prev) => ({ ...prev, custody: undefined }));
    }
  };

  const handleDocumentTypeChange = (e: any) => {
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      documentTypes,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof otherParent>("documentTypeId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof otherParent>("documentTypeName"),
        },
      ],
      setOtherParent
    );
  };

  const handleReasonNonExistenceOibTypeChange = (e: any) => {
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      reasonNonExistenceOibTypes,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof otherParent>("reasonNonExistenceOibId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof otherParent>("reasonNonExistenceOibName"),
        },
      ],
      setOtherParent
    );
  };

  const handleSexChange = (e: any) => {
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      sexes,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof otherParent>("sexId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof otherParent>("sexName"),
        },
      ],
      setOtherParent
    );
  };

  const handleProfessionalQualificationChange = (e: any) => {
    handleMultipleChange(
      e,
      nameof<CodeBook>("id"),
      professionalQualifications,
      [
        {
          valueProp: nameof<CodeBook>("id"),
          stateProp: nameof<typeof otherParent>("professionalQualificationId"),
        },
        {
          valueProp: nameof<CodeBook>("name"),
          stateProp: nameof<typeof otherParent>(
            "professionalQualificationName"
          ),
        },
      ],
      setOtherParent
    );
  };

  const handleStep = () => {
    if (otherParent) {
      const isValid = doValidations();
      if (
        isValid &&
        otherParent &&
        otherParent.isOibNotExistent === false &&
        otherParent.integrationResultStatus
      ) {
        if (
          otherParent.integrationResultStatus ===
          OtherParentDataCheckStatusEnum.Success
        ) {
          dispatch(updateRequestOtherParent(otherParent));
        } else if (
          otherParent.integrationResultStatus ===
          OtherParentDataCheckStatusEnum.NoCustody
        ) {
          if (
            otherParent?.documents === null ||
            otherParent?.documents?.length < 1 ||
            !otherParent.isConfirmedParentChildRelationByDocument
          ) {
            setIsMessageModalOpened((prev) => !prev);
            return false;
          } else {
            dispatch(updateRequestOtherParent(otherParent));
          }
        }
      } else if (
        isValid &&
        otherParent &&
        otherParent.isOibNotExistent === true
      ) {
        dispatch(updateRequestOtherParent(otherParent));
      } else {
        setShowDataCheckMessage(true);
        return false;
      }
    }
  };

  const {
    isDocumentsModalOpen,
    handleUploadDocuments,
    handleUploadExistingDocument,
    handleConfirmDocumentsModal,
    handleCloseDocumentsModal,
    handleToggleDocumentsModal,
    handleDownloadDocument,
    handleRemoveDocumentFromList,
    handleRemoveDocument,
  } = useOtherParentDocuments({ setOtherParent: setOtherParent });

  return (
    <>
      <Row>
        <Col md={12}>
          <InfoPanel
            message={t("person.mainInfoMessage")}
            color="success"
            className={styles.mainInfoMessage}
            fullWidth
          />
          {otherParent.isOibNotExistent === false && (
            <InfoPanel
              message={t("person.otherParentWarningMessage")}
              color="warning"
              className={styles.mainInfoMessage}
              fullWidth
            />
          )}
        </Col>
      </Row>
      <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          <Typography component="h2" variant="largeBold">
            {t("otherParentDataStep.header")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowBasicInfoSpace}>
        <Col md={12}>
          <Typography component="h3" variant="smallHeading">
            {t("request.basicInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.rowDataCheck}>
        <Col md={12}>
          {handleGetCheckMessage()}
          {otherParent.isOibNotExistent === false && (
            <Button
              color="primary"
              outline
              className={styles.checkDataBtn}
              disabled={isCheckingOtherParentData}
              onClick={handleCheckData}
            >
              {t("otherParentDataStep.checkData")}
            </Button>
          )}
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            required
            type="text"
            label={t("person.firstName")}
            name={nameof<typeof otherParent>("firstName")}
            value={otherParent.firstName || ""}
            onChange={(e) => handleChange(e, setOtherParent)}
            onBlur={handleBlur}
            errorText={errors?.firstName || checkDataErrors?.firstName}
          />
        </Col>
        <Col md={4}>
          <TextField
            required
            type="text"
            label={t("person.lastName")}
            name={nameof<typeof otherParent>("lastName")}
            value={otherParent.lastName || ""}
            onChange={(e) => handleChange(e, setOtherParent)}
            onBlur={handleBlur}
            errorText={errors?.lastName || checkDataErrors?.lastName}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpace}>
        <Col md={4}>
          <TextField
            required={otherParent.isOibNotExistent === false}
            type="text"
            label={t("person.oib")}
            name={nameof<typeof otherParent>("oib")}
            value={otherParent.oib || ""}
            onChange={(e) => handleChange(e, setOtherParent)}
            onBlur={handleBlur}
            helperText={t("common.oibApi")}
            errorText={errors?.oib || checkDataErrors?.oib}
            maxLength={11}
            disabled={otherParent.isOibNotExistent}
          />
        </Col>
        <Col md={4}>
          <Checkbox
            label={t("otherParentDataStep.oibNotExistent")}
            checked={otherParent.isOibNotExistent}
            onChange={(e) => handleToggleIsOibNotExistent()}
          />
        </Col>
      </Row>
      {otherParent &&
        ((otherParent.integrationResultStatus !== null &&
          (otherParent.integrationResultStatus ===
            OtherParentDataCheckStatusEnum.Success ||
            (otherParent.integrationResultStatus ===
              OtherParentDataCheckStatusEnum.NoCustody &&
              user.roleId !== KindergartenRoleEnum.Parent))) ||
          otherParent.isOibNotExistent) && (
          <>
            <>
              {otherParent.isOibNotExistent && (
                <Row className={styles.rowSpace}>
                  <Col md={4}>
                    <TextField
                      required={otherParent.isOibNotExistent}
                      type="text"
                      label={t("person.documentNumber")}
                      name={nameof<typeof otherParent>("documentNumber")}
                      value={otherParent.documentNumber}
                      onChange={(e) => handleChange(e, setOtherParent)}
                      errorText={errors?.documentNumber}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      required={otherParent.isOibNotExistent}
                      label={t("person.documentTypeId")}
                      name={nameof<typeof otherParent>("documentTypeId")}
                      value={otherParent.documentTypeId || undefined}
                      onChange={handleDocumentTypeChange}
                      selectData={
                        {
                          valueProp: "id",
                          textProp: "name",
                          data: documentTypes || [],
                        } as SelectData<CodeBook>
                      }
                      errorText={errors?.documentTypeId}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      required={otherParent.isOibNotExistent}
                      label={t("person.reasonNonExistenceOibId")}
                      name={nameof<typeof otherParent>(
                        "reasonNonExistenceOibId"
                      )}
                      value={otherParent.reasonNonExistenceOibId || undefined}
                      onChange={handleReasonNonExistenceOibTypeChange}
                      selectData={
                        {
                          valueProp: "id",
                          textProp: "name",
                          data: reasonNonExistenceOibTypes || [],
                        } as SelectData<CodeBook>
                      }
                      errorText={errors?.reasonNonExistenceOibId}
                    />
                  </Col>
                </Row>
              )}
              <Row className={styles.rowSpace}>
                <Col md={2}>
                  <TextField
                    type="number"
                    label={t("person.yearOfBirth")}
                    required={otherParent.isOibNotExistent}
                    name={nameof<typeof otherParent>("yearOfBirth")}
                    value={otherParent.yearOfBirth}
                    onChange={(e) => handleChange(e, setOtherParent)}
                    helperText={
                      otherParent.isOibNotExistent
                        ? ""
                        : t("common.birthRegistry")
                    }
                    plaintext={otherParent.isOibNotExistent === false}
                    errorText={errors?.yearOfBirth}
                  />
                </Col>
                <Col md={2}>
                  <Select
                    label={t("person.sex")}
                    required={otherParent.isOibNotExistent}
                    name={nameof<typeof otherParent>("sexId")}
                    value={otherParent.sexId || undefined}
                    onChange={handleSexChange}
                    selectData={
                      {
                        valueProp: "id",
                        textProp: "name",
                        data: sexes,
                      } as SelectData<CodeBook>
                    }
                    errorText={errors?.sexId}
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    required={otherParent.isOibNotExistent}
                    type="text"
                    label={t("person.profession")}
                    name={nameof<typeof otherParent>("professionName")}
                    value={otherParent.professionName}
                    onChange={(e) => handleChange(e, setOtherParent)}
                    errorText={errors?.professionName}
                  />
                </Col>
                <Col md={4}>
                  <Select
                    label={t("person.professionalQualification")}
                    required={otherParent.isOibNotExistent}
                    name={nameof<typeof otherParent>(
                      "professionalQualificationId"
                    )}
                    value={otherParent.professionalQualificationId || undefined}
                    onChange={handleProfessionalQualificationChange}
                    selectData={
                      {
                        valueProp: "id",
                        textProp: "name",
                        data: professionalQualifications || [],
                      } as SelectData<CodeBook>
                    }
                    errorText={errors?.professionalQualificationId}
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.email")}
                    name={nameof<typeof otherParent>("email")}
                    value={otherParent.email || ""}
                    onChange={(e) => handleChange(e, setOtherParent)}
                    errorText={errors?.email}
                  />
                </Col>
                <Col md={4}>
                  <TextField
                    type="text"
                    label={t("person.phoneNumber")}
                    name={nameof<typeof otherParent>("phoneNumber")}
                    value={otherParent.phoneNumber || ""}
                    onChange={(e) => handleChange(e, setOtherParent)}
                  />
                </Col>
                <Col md={4}>
                  <Select
                    required
                    label={t("person.parentshipType")}
                    name={nameof<typeof otherParent>("parentshipTypeId")}
                    value={otherParent.parentshipTypeId || undefined}
                    onChange={handleParentshipTypeChange}
                    selectData={
                      {
                        valueProp: "id",
                        textProp: "name",
                        data: parentshipTypes || [],
                      } as SelectData<CodeBook>
                    }
                    errorText={errors?.parentshipTypeId}
                  />
                </Col>
              </Row>

              {otherParent?.parentshipTypeId ===
              ParentshipTypeEnum.Custodian ? (
                <Row className={styles.rowSpace}>
                  <Col md={4}>
                    <TextField
                      required
                      type="text"
                      label={`${t("person.custodian")}`}
                      name={nameof<typeof otherParent>("custody")}
                      value={otherParent?.custody || ""}
                      onChange={(e) => handleChange(e, setOtherParent)}
                      errorText={errors?.custody}
                    />
                  </Col>
                </Row>
              ) : (
                <></>
              )}
              {otherParent.isOibNotExistent === false && (
                <Row className={styles.rowHeadingSpace}>
                  <Col md={12}>
                    <InfoPanel
                      message={
                        otherParent.isCustodyConfirmed
                          ? t(
                              "applicantAndOtherParentSteps.parentChildRelationConfirmed"
                            )
                          : t(
                              "applicantAndOtherParentSteps.parentChildRelationNotConfirmed"
                            )
                      }
                      color={
                        otherParent.isCustodyConfirmed ? "success" : "danger"
                      }
                    />
                  </Col>
                </Row>
              )}
              <Row
                className={
                  styles.rowHeadingSpace + " " + styles.rowDocumentation
                }
              >
                <Col md={12}>
                  <Typography component="h3" variant="smallHeading">
                    {t("documentUpload.additionalParentDocumentation")}
                  </Typography>
                </Col>
              </Row>
              {otherParent.isOibNotExistent === false &&
                !otherParent.isCustodyConfirmed && (
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      <InfoPanel
                        message={t(
                          "applicantAndOtherParentSteps.attachDocument"
                        )}
                        color="warning"
                      />
                    </Col>
                  </Row>
                )}
              <Row className={styles.rowHeadingSpace}>
                <Col md={12}>
                  <InfoPanel
                    message={t("documentUpload.infoMessage")}
                    color="success"
                  />
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={12}>
                  <Button
                    color="primary"
                    disabled={
                      otherParent.documents &&
                      otherParent.documents.length >= 10
                        ? true
                        : false
                    }
                    outline
                    icon={<UploadIcon />}
                    onClick={handleToggleDocumentsModal}
                  >
                    {t("documentUpload.additionalDocumentationAttach")}
                  </Button>
                  {
                    <DocumentsModal
                      open={isDocumentsModalOpen}
                      allDocuments={allDocuments}
                      onClose={handleCloseDocumentsModal}
                      onConfirm={handleConfirmDocumentsModal}
                      onUpload={handleUploadDocuments}
                      onRemove={handleRemoveDocument}
                      onDownload={handleDownloadDocument}
                      onExistingDocumentClick={handleUploadExistingDocument}
                    />
                  }
                </Col>
              </Row>
              <Row className={styles.rowSpace}>
                <Col md={12}>
                  <Col md={12}>
                    <Typography variant="smallHeading">
                      {t("documentUpload.additionalDocumentationList")}
                    </Typography>
                  </Col>
                  {
                    <DocumentList
                      documents={otherParent.documents || []}
                      onRemoveClick={handleRemoveDocumentFromList}
                      onDocumentClick={handleDownloadDocument}
                      fullWidth
                    />
                  }
                </Col>
              </Row>
              {otherParent.isOibNotExistent === false &&
                !otherParent.isCustodyConfirmed &&
                otherParent?.documents != null &&
                otherParent?.documents?.length > 0 && (
                  <Row>
                    <Col md={12}>
                      <Checkbox
                        label={t(
                          "applicantAndOtherParentSteps.confirmationParentChildRelationByDocument"
                        )}
                        name={nameof<typeof otherParent>(
                          "isConfirmedParentChildRelationByDocument"
                        )}
                        checked={
                          otherParent?.isConfirmedParentChildRelationByDocument
                        }
                        value={
                          otherParent?.isConfirmedParentChildRelationByDocument
                        }
                        onChange={(e) => handleChange(e, setOtherParent)}
                      />
                    </Col>
                  </Row>
                )}
            </>
            <Modal
              type={"confirm"}
              title={t(
                "applicantAndOtherParentSteps.confirmationThatTheDocumentIsAttachedTitle"
              )}
              cancelText={t("common.no")}
              confirmText={t("common.yes")}
              open={isConfirmModalOpened}
              onClose={handleToggleConfirmModal}
              onConfirm={handleConfirmActionModal}
            >
              {t(
                "applicantAndOtherParentSteps.confirmationThatTheDocumentIsAttached"
              )}
            </Modal>

            <Modal
              type={"message"}
              title={t(
                "applicantAndOtherParentSteps.documentIsNotAttachedTitle"
              )}
              confirmText={t("common.close")}
              open={isMessageModalOpened}
              onConfirm={handleToggleMessageModal}
              onClose={handleToggleMessageModal}
            >
              {t("applicantAndOtherParentSteps.documentIsNotAttached")}
            </Modal>
          </>
        )}
    </>
  );
}
